import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Milestone } from "@/hooks/use-milestones";

const PHASE_LABELS: Record<string, string> = {
  pre_construction: "Pre-Construction",
  construction: "Construction",
  post_construction: "Post-Construction",
};

function formatDate(date: string | null | undefined): string {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function todayISO(): string {
  const iso = new Date().toISOString().split("T")[0];
  return iso ?? "";
}

type RowStatus = "complete" | "overdue" | "at_risk" | "on_track" | "unscheduled";

function computeStatus(m: Milestone): RowStatus {
  if (m.actualDate) return "complete";
  if (!m.forecastDate && !m.contractDate) return "unscheduled";
  const today = todayISO();
  if (m.forecastDate && m.forecastDate < today) return "overdue";
  if (m.forecastDate && m.contractDate && m.forecastDate > m.contractDate) return "at_risk";
  return "on_track";
}

const STATUS_META: Record<RowStatus, { label: string; className: string }> = {
  complete: { label: "Complete", className: "bg-emerald-100 text-emerald-700" },
  overdue: { label: "Overdue", className: "bg-red-100 text-red-700" },
  at_risk: { label: "At Risk", className: "bg-amber-100 text-amber-700" },
  on_track: { label: "On Track", className: "bg-blue-100 text-blue-700" },
  unscheduled: { label: "Unscheduled", className: "bg-gray-100 text-gray-600" },
};

const DATE_ROWS: { type: "contract" | "forecast" | "actual"; label: string; diamond: string }[] = [
  { type: "contract", label: "Contract Date", diamond: "bg-white border-gray-400" },
  { type: "forecast", label: "Forecast Date", diamond: "bg-amber-400 border-amber-600" },
  { type: "actual", label: "Actual Date", diamond: "bg-emerald-500 border-emerald-700" },
];

function diffDays(a: string | null | undefined, b: string | null | undefined): number | null {
  if (!a || !b) return null;
  const ms = new Date(a).getTime() - new Date(b).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function Variance({
  days,
  labelLater,
  labelEarlier,
}: {
  days: number;
  labelLater: string;
  labelEarlier: string;
}) {
  if (days === 0) {
    return <span className="text-xs text-muted-foreground">on target</span>;
  }
  const abs = Math.abs(days);
  const text = `${abs} day${abs === 1 ? "" : "s"} ${days > 0 ? labelLater : labelEarlier}`;
  const color = days > 0 ? "text-red-600" : "text-emerald-600";
  return <span className={`text-xs font-medium ${color}`}>{text}</span>;
}

interface MilestoneDetailDialogProps {
  milestone: Milestone | null;
  projectName?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MilestoneDetailDialog({
  milestone,
  projectName,
  open,
  onOpenChange,
}: MilestoneDetailDialogProps) {
  if (!milestone) return null;
  const status = computeStatus(milestone);
  const meta = STATUS_META[status];
  const forecastVsContract = diffDays(milestone.forecastDate, milestone.contractDate);
  const actualVsContract = diffDays(milestone.actualDate, milestone.contractDate);
  const actualVsForecast = diffDays(milestone.actualDate, milestone.forecastDate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-[#334155]">
            {milestone.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.className}`}
            >
              {meta.label}
            </span>
            <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
              {PHASE_LABELS[milestone.phase] ?? milestone.phase}
            </span>
            {milestone.isTemplateDerived && (
              <span
                className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                title={milestone.templateName ?? "Template-derived"}
              >
                Template
              </span>
            )}
            {projectName && (
              <span className="ml-auto text-xs text-muted-foreground">{projectName}</span>
            )}
          </div>

          <div className="rounded-lg border bg-muted/20 p-3">
            <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Timeline
            </div>
            <div className="space-y-2">
              {DATE_ROWS.map((row) => {
                const date =
                  milestone[`${row.type}Date` as "contractDate" | "forecastDate" | "actualDate"];
                return (
                  <div key={row.type} className="flex items-center gap-3">
                    <span className={`h-2.5 w-2.5 shrink-0 rotate-45 border ${row.diamond}`} />
                    <span className="w-28 text-sm text-gray-600">{row.label}</span>
                    <span className="text-sm font-medium text-gray-900">{formatDate(date)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {(forecastVsContract !== null ||
            actualVsContract !== null ||
            actualVsForecast !== null) && (
            <div className="rounded-lg border p-3">
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Variance
              </div>
              <div className="space-y-1">
                {forecastVsContract !== null && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Forecast vs Contract</span>
                    <Variance days={forecastVsContract} labelLater="late" labelEarlier="early" />
                  </div>
                )}
                {actualVsContract !== null && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Actual vs Contract</span>
                    <Variance days={actualVsContract} labelLater="late" labelEarlier="early" />
                  </div>
                )}
                {actualVsForecast !== null && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Actual vs Forecast</span>
                    <Variance days={actualVsForecast} labelLater="late" labelEarlier="early" />
                  </div>
                )}
              </div>
            </div>
          )}

          {milestone.description && (
            <div className="rounded-lg border p-3">
              <div className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Description
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-line">{milestone.description}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button asChild className="bg-[#334155] hover:bg-[#415A77] text-white">
            <Link
              to="/projects/$projectId"
              params={{ projectId: milestone.projectId }}
              search={{ tab: "overview" }}
              onClick={() => onOpenChange(false)}
            >
              Open in Project
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
